'use client'
import { _get } from 'utils/shared'
import footerNavigation from 'components/Footer/footerNavigation.json'
import { Footer } from 'components/Footer/UniversalFooter/Footer/Footer'
import { useAnalyticsTrackEvent } from 'hooks/useAnalyticsTrackEvent'
import { Typography, TypographyLongForm } from 'components/Contentful'
import { MediaImageStandard } from 'components/Contentful'
import { FdrTheme, AchieveTheme, AscendThemeProvider } from '@achieve/ascend'
import { useContext, useMemo } from 'react'
import { LayoutContext } from 'providers/LayoutProvider'
import SimpleFooter from 'components/Footer/SimpleFooter/SimpleFooter'
import { SITE_ENVIRONMENTS } from 'constants/websites'
import classNames from 'classnames'
import { FdrPage, BillsPage } from 'utils/environmentConfig'
import { withUuid } from 'utils/shared'
import { resolveCopyrightMessage } from 'components/Footer/resolveCopyrightMessage'

let theme
switch (process.env.NEXT_PUBLIC_SITE_ENVIRONMENT) {
  case SITE_ENVIRONMENTS.ACHIEVE:
    theme = AchieveTheme
    break
  case SITE_ENVIRONMENTS.FDR:
    theme = FdrTheme
    break
  default:
    theme = AchieveTheme
}

export default function FooterContainer({
  disabledRoutes,
  content,
  pageDisclosure = '',
  secondaryDisclosures,
  showNav,
  hiddenNavSection = [],
  showFooterSocialIcons,
}) {
  const { analyticsTrackEvent } = useAnalyticsTrackEvent()
  const { state: { siteEnv, siteInfo } = {} } = useContext(LayoutContext)
  const finalShowNav = typeof showNav == 'boolean' ? showNav : siteInfo?.showNavFooter
  const disclosureList = useMemo(
    () =>
      withUuid(pageDisclosure ? [pageDisclosure, ...secondaryDisclosures] : secondaryDisclosures),
    [pageDisclosure, secondaryDisclosures]
  )
  let footerMenu = footerNavigation?.[siteEnv || 'achieve']?.footerMenus || []
  const brandCopy = _get(content, 'logoHelpText') || siteInfo?.footerBrandCopy
  const logoHelpSubtext = _get(content, 'logoHelpSubtext')
  const menuContent = _get(content, 'footerItems')
  const jsonConfig = _get(content, 'config')
  const uiComponent = _get(content, 'uiComponent', 'DefaultFooter')
  const markersResources = _get(content, 'markers') ?? footerNavigation.markers
  const copyrightFallback = `${siteInfo?.url}. All rights reserved.`
  const copyrightMessage = resolveCopyrightMessage(
    _get(content, 'copyrightMessage'),
    copyrightFallback
  )
  if (menuContent) {
    footerMenu = menuContent.reduce((acc, element) => {
      const subMenu = (element?.fields?.gridItems ?? [])
        .map((subElement) => {
          const label = _get(subElement, 'fields.linkText.content[0].content[0].value')
          const name = _get(subElement, 'fields.name')
          const url = _get(subElement, 'fields.linkHref')
          return { label: label || name, url }
        })
        .filter(({ url }) => url?.length && !disabledRoutes.includes(url))

      if (subMenu.length) {
        const menuLabel = _get(element, 'fields.name')
        if (!hiddenNavSection.includes(menuLabel)) {
          acc.push({ menuLabel, menuItems: subMenu })
        }
      }

      return acc
    }, [])
  } else if (content) {
    footerMenu = []
  }

  if (uiComponent === 'SimpleFooter') {
    return (
      <SimpleFooter
        footerNavigation={footerMenu}
        pageDisclosure={disclosureList?.map((dataDisclosure) => (
          <div key={dataDisclosure.uuid}>
            <TypographyLongForm content={dataDisclosure} variant="bodyXs" fontWeight="regular" />
          </div>
        ))}
        copyrightMessage={copyrightMessage}
      />
    )
  }

  let logoImageDefault = '/2026_Achieve_Logo_footer.svg'
  if (siteEnv === FdrPage) {
    logoImageDefault = '/multi-site-assets/fdr-logo.svg'
  }
  if (siteEnv === BillsPage) {
    logoImageDefault = '/multi-site-assets/bills-logo.svg'
  }

  return (
    <AscendThemeProvider theme={theme}>
      <Footer
        siteEnv={siteEnv}
        mainName={siteInfo?.mainName}
        footerNavigation={footerMenu}
        pageDisclosure={disclosureList?.map((dataDisclosure) => (
          <div key={dataDisclosure.uuid}>
            <TypographyLongForm content={dataDisclosure} variant="bodyXs" fontWeight="regular" />
          </div>
        ))}
        markersSection={markersResources?.map((marker, index) => {
          return (
            <div key={`marker-img-${index}`}>
              <MediaImageStandard
                alt={marker.fields.name}
                content={marker.fields.image}
                width={90}
                height={60}
                objectFit="contain"
              />
              {marker.fields.subtitle !== '.' && (
                <Typography
                  content={marker.fields.subtitle}
                  variant="bodyXs"
                  fontWeight="regular"
                  style={{ textAlign: 'center', marginTop: -2 }}
                  fontSize="8px"
                />
              )}
            </div>
          )
        })}
        logoImage={siteInfo?.footerLogo}
        logoHelpSubtext={logoHelpSubtext}
        logoImageDefault={logoImageDefault}
        brandCopy={brandCopy}
        trackingCB={analyticsTrackEvent}
        copyrightMessage={copyrightMessage}
        showNav={finalShowNav}
        showFooterSocialIcons={showFooterSocialIcons}
        backgroundColor={
          AchieveTheme?.backgroundColors?.primary || AchieveTheme?.ascend.colors?.blue[50]
        }
        schema={jsonConfig?.schema}
        config={jsonConfig}
        className={classNames('transition-transform duration-300 ease-in-out transform')}
      />
    </AscendThemeProvider>
  )
}
